/**
 * Markdown → PDF and DOCX export utilities.
 *
 * PDF:  Renders into a hidden iframe and triggers the browser's native
 *       Print-to-PDF dialog. This is the most reliable cross-browser
 *       approach — no html2canvas dependency, proper pagination, and
 *       crisp text rendering.
 *
 * DOCX: Wraps HTML in Word-compatible MHTML format → .doc file that
 *       Word and Google Docs open natively with formatting.
 */

// ─── Markdown → HTML ─────────────────────────────────────────────────

function markdownToHtml(md: string): string {
  // Pass 1: protect fenced code blocks
  const codeBlocks: string[] = []
  let src = md.replace(/```[\w]*\n([\s\S]*?)```/g, (_m, code: string) => {
    const idx = codeBlocks.length
    codeBlocks.push(
      `<pre><code>${escapeHtml(code.trimEnd())}</code></pre>`
    )
    return `%%CB${idx}%%`
  })

  // Pass 2: protect tables
  const tableBlocks: string[] = []
  src = src.replace(
    /^(\|.+\|)\n(\|[\s:|-]+\|)\n((?:\|.+\|\n?)+)/gm,
    (_m, hdr: string, _sep: string, body: string) => {
      const split = (r: string) =>
        r.split("|").slice(1, -1).map((c: string) => c.trim())
      const ths = split(hdr)
        .map((h: string) => `<th>${inline(h)}</th>`)
        .join("")
      const trs = body
        .trim()
        .split("\n")
        .map(
          (r: string) =>
            `<tr>${split(r).map((c: string) => `<td>${inline(c)}</td>`).join("")}</tr>`
        )
        .join("")
      const idx = tableBlocks.length
      tableBlocks.push(
        `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`
      )
      return `%%TB${idx}%%`
    }
  )

  // Pass 3: line-by-line
  const lines = src.split("\n")
  const out: string[] = []
  let listTag: "ul" | "ol" | "" = ""

  const closeLists = () => {
    if (listTag) {
      out.push(`</${listTag}>`)
      listTag = ""
    }
  }

  for (const raw of lines) {
    const line = raw.trim()

    // Placeholders
    if (/^%%CB\d+%%$/.test(line)) {
      closeLists()
      out.push(codeBlocks[parseInt(line.slice(4, -2))])
      continue
    }
    if (/^%%TB\d+%%$/.test(line)) {
      closeLists()
      out.push(tableBlocks[parseInt(line.slice(4, -2))])
      continue
    }

    if (!line) { closeLists(); continue }

    // HR
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) { closeLists(); out.push("<hr>"); continue }

    // Headers
    const hm = line.match(/^(#{1,6})\s+(.+)/)
    if (hm) {
      closeLists()
      const n = hm[1].length
      out.push(`<h${n}>${inline(hm[2])}</h${n}>`)
      continue
    }

    // UL
    const um = line.match(/^[*\-+]\s+(.+)/)
    if (um) {
      if (listTag !== "ul") { closeLists(); out.push("<ul>"); listTag = "ul" }
      out.push(`<li>${inline(um[1])}</li>`)
      continue
    }

    // OL
    const om = line.match(/^\d+\.\s+(.+)/)
    if (om) {
      if (listTag !== "ol") { closeLists(); out.push("<ol>"); listTag = "ol" }
      out.push(`<li>${inline(om[1])}</li>`)
      continue
    }

    // Blockquote
    if (line.startsWith("> ")) {
      closeLists()
      out.push(`<blockquote>${inline(line.slice(2))}</blockquote>`)
      continue
    }

    // Paragraph
    closeLists()
    out.push(`<p>${inline(line)}</p>`)
  }
  closeLists()
  return out.join("\n")
}

function inline(t: string): string {
  return t
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
}

function escapeHtml(t: string): string {
  return t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

// ─── Shared styles ──────────────────────────────────────────────────

const PDF_STYLES = `
@page {
  size: letter;
  margin: 0.75in 0.85in;
}
* { box-sizing: border-box; }
body {
  font-family: 'Times New Roman', Georgia, serif;
  font-size: 12pt;
  line-height: 1.65;
  color: #111;
  margin: 0;
  padding: 0;
}
h1 {
  font-size: 20pt;
  font-weight: bold;
  margin: 28pt 0 10pt 0;
  color: #111;
  border-bottom: 1.5pt solid #ccc;
  padding-bottom: 6pt;
  page-break-after: avoid;
}
h2 {
  font-size: 16pt;
  font-weight: bold;
  margin: 22pt 0 8pt 0;
  color: #111;
  page-break-after: avoid;
}
h3 {
  font-size: 13pt;
  font-weight: bold;
  margin: 18pt 0 6pt 0;
  color: #222;
  page-break-after: avoid;
}
h4 { font-size: 12pt; font-weight: bold; margin: 14pt 0 4pt 0; color: #333; }
p { margin: 6pt 0; orphans: 3; widows: 3; }
ul, ol { margin: 6pt 0; padding-left: 22pt; }
li { margin: 3pt 0; }
table {
  border-collapse: collapse;
  width: 100%;
  margin: 12pt 0;
  page-break-inside: avoid;
}
th {
  border: 1pt solid #999;
  padding: 6pt 10pt;
  background: #f4f4f4;
  font-weight: bold;
  font-size: 10pt;
  text-align: left;
}
td {
  border: 1pt solid #999;
  padding: 6pt 10pt;
  font-size: 10pt;
}
blockquote {
  margin: 10pt 0;
  padding: 6pt 14pt;
  border-left: 3pt solid #ccc;
  color: #444;
  font-style: italic;
}
pre {
  background: #f6f6f6;
  padding: 10pt 14pt;
  border: 1pt solid #ddd;
  font-family: 'Courier New', monospace;
  font-size: 9pt;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  page-break-inside: avoid;
}
code {
  font-family: 'Courier New', monospace;
  font-size: 10pt;
  background: #f0f0f0;
  padding: 1pt 3pt;
}
hr { border: none; border-top: 1pt solid #ccc; margin: 16pt 0; }
a { color: #1a5fb4; }
strong { font-weight: bold; }
em { font-style: italic; }
`

// ─── PDF Export (print-based) ────────────────────────────────────────

export async function downloadMarkdownAsPdf(
  markdown: string,
  filename: string
): Promise<void> {
  const bodyHtml = markdownToHtml(markdown)

  const iframe = document.createElement("iframe")
  iframe.style.cssText = "position:fixed;left:-10000px;top:-10000px;width:0;height:0;border:none;"
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
  if (!iframeDoc) {
    document.body.removeChild(iframe)
    throw new Error("Could not access iframe document")
  }

  iframeDoc.open()
  iframeDoc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(filename)}</title>
<style>${PDF_STYLES}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`)
  iframeDoc.close()

  // Wait for content to render
  await new Promise<void>((resolve) => {
    if (iframe.contentWindow) {
      iframe.contentWindow.onload = () => resolve()
      // Fallback if onload already fired
      setTimeout(resolve, 500)
    } else {
      setTimeout(resolve, 500)
    }
  })

  try {
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  } finally {
    // Remove iframe after a delay to let print dialog finish
    setTimeout(() => {
      document.body.removeChild(iframe)
    }, 1000)
  }
}

// ─── DOCX Export ─────────────────────────────────────────────────────

export function downloadMarkdownAsDocx(
  markdown: string,
  filename: string
): void {
  const bodyHtml = markdownToHtml(markdown)

  const wordHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:w="urn:schemas-microsoft-com:office:word"
xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<meta name="ProgId" content="Word.Document">
<meta name="Generator" content="Microsoft Word 15">
<!--[if gte mso 9]>
<xml>
<w:WordDocument>
<w:View>Print</w:View>
<w:Zoom>100</w:Zoom>
<w:DoNotOptimizeForBrowser/>
</w:WordDocument>
</xml>
<![endif]-->
<style>
body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.6; color: #111; }
h1 { font-size: 18pt; font-weight: bold; margin: 24pt 0 8pt 0; border-bottom: 1pt solid #ccc; padding-bottom: 4pt; }
h2 { font-size: 15pt; font-weight: bold; margin: 20pt 0 6pt 0; }
h3 { font-size: 13pt; font-weight: bold; margin: 16pt 0 4pt 0; color: #333; }
h4 { font-size: 12pt; font-weight: bold; margin: 14pt 0 4pt 0; }
p { font-size: 11pt; margin: 6pt 0; }
ul, ol { margin: 6pt 0; padding-left: 24pt; }
li { font-size: 11pt; margin: 3pt 0; }
table { border-collapse: collapse; width: 100%; margin: 10pt 0; }
th { border: 1pt solid #999; padding: 5pt 8pt; background: #f0f4f8; font-weight: bold; font-size: 10pt; }
td { border: 1pt solid #999; padding: 5pt 8pt; font-size: 10pt; }
blockquote { margin: 10pt 0; padding: 6pt 12pt; border-left: 3pt solid #ccc; color: #555; }
pre { background: #f5f5f5; padding: 8pt; border: 1pt solid #ddd; font-family: 'Courier New', monospace; font-size: 9pt; white-space: pre-wrap; }
code { font-family: 'Courier New', monospace; font-size: 10pt; background: #f0f0f0; padding: 1pt 3pt; }
hr { border: none; border-top: 1pt solid #ccc; margin: 14pt 0; }
</style>
</head>
<body>
${bodyHtml}
</body>
</html>`

  const blob = new Blob(["\ufeff", wordHtml], { type: "application/msword" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename.replace(/\.(docx?|md)$/, "") + ".doc"
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Markdown Export ─────────────────────────────────────────────────

export function downloadMarkdownAsFile(
  markdown: string,
  filename: string
): void {
  const blob = new Blob([markdown], { type: "text/markdown" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename.endsWith(".md") ? filename : `${filename}.md`
  a.click()
  URL.revokeObjectURL(url)
}
